/**
 * Client d'API typé de l'application INTERNE.
 *
 * L'espace client a le sien, `client/api.ts`, et les deux ne partagent que la
 * plomberie HTTP — qui ne connaît aucune notion d'identité (ADR-0012).
 *
 * Un seul endroit sait parler à l'API : le reste de l'application ne connaît
 * que des fonctions et des types. Le jour où un point d'API change de forme, le
 * compilateur dit exactement quels écrans sont concernés.
 *
 * `credentials: "include"` sur chaque appel : l'authentification passe par un
 * cookie `httpOnly` (plan du LOT 0, §0.3). Il n'y a donc AUCUN jeton à stocker
 * côté navigateur — c'est précisément ce qu'on cherchait en écartant le JWT.
 */

// --- Types du domaine, calqués sur les schémas de l'API ----------------------

/** Un profil porté, avec le libellé que l'Administrateur lui a donné (ADR-0035). */
export type ProfilPorte = { code: string; libelle: string };

export type Identite = {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  profils: ProfilPorte[];
  permissions: string[];
  /** La famille de périmètre (ADR-0035) ; le terrain est limité à ses affectations. */
  famille: "AFFECTATIONS" | "SES_COMMANDES" | "TOUT";
  /** Le terrain ne lit pas les fournisseurs : le serveur le dit, l'écran masque. */
  lit_les_fournisseurs: boolean;
  /**
   * ADR-0047 : jusqu'où ce compte valide un écart de prix fournisseur, en % ;
   * `null`, sans plafond — si `permissions` porte la validation.
   */
  plafond_ecart_prix: string | null;
  /** Ticket #50 : de même, pour un écart de frais fournisseur. */
  plafond_ecart_frais: string | null;
};

export type EtatEtape = "NON_APPLICABLE" | "A_FAIRE" | "EN_COURS" | "EN_ALERTE" | "TERMINEE";

export type Etape = {
  etape: string;
  libelle: string;
  etat: EtatEtape;
  detail: string | null;
};

export type Adresse = {
  ligne1: string;
  ligne2: string | null;
  code_postal: string;
  ville: string;
};

export type Lot = {
  id: string;
  code_lot: string;
  sequence: number;
  intitule: string;
  categorie: string;
  // Le libellé vient du référentiel administrable (ADR-0030), pas d'une table
  // du front : un renommage par l'administrateur se voit sans redéploiement.
  categorie_libelle: string;
  // Celle de la commande (ADR-0031) ; absente à l'emport sans pose.
  adresse_realisation: Adresse | null;
  // Les natures de la commande : elles disent quelle preuve de réalisation la
  // clôture du lot attend (ADR-0033).
  mode_remise: "EMPORT" | "LIVRAISON";
  avec_pose: boolean;
  montant_ht_quote_part: number | null;
  responsable_operationnel_id: string | null;
  commentaires: string | null;
  commande_client_id: string;
  numero_commande_client: string;
  statut: string;
  statut_affiche: string;
  etapes: Etape[];
  prochaine_action: Etape | null;
};

export type Client = {
  id: string;
  type: string;
  designation: string;
  raison_sociale: string | null;
  civilite: string | null;
  nom: string;
  prenom: string | null;
  email: string | null;
  telephone: string | null;
  telephone_secondaire: string | null;
  adresse_ligne1: string | null;
  adresse_ligne2: string | null;
  code_postal: string | null;
  ville: string | null;
  notes: string | null;
  anonymise: boolean;
  nombre_commandes: number;
};

/**
 * La fiche fournisseur (LOT 2). Un référentiel : aucun périmètre ne la filtre.
 * `notes_generales` se lit avec la fiche ; `notes_confidentielles` est nul sans la
 * permission « Consulter les notes confidentielles des fournisseurs » — LAB y
 * consigne ses conditions d'achat, sans lien avec les montants.
 */
export type StatutFournisseur = "ACTIF" | "SUSPENDU" | "ANCIEN";

export type Fournisseur = {
  id: string;
  nom: string;
  siren: string | null;
  siret: string | null;
  adresse_ligne1: string | null;
  code_postal: string | null;
  ville: string | null;
  email: string | null;
  telephone: string | null;
  delai_moyen_jours: number | null;
  statut: StatutFournisseur;
  /** Le code du référentiel ; le libellé suit, même pour une catégorie désactivée. */
  categorie: string | null;
  categorie_libelle: string | null;
  notes_generales: string | null;
  notes_confidentielles: string | null;
};

export type EnvoiCourriel = {
  le: string;
  reussi: boolean;
  destinataire: string | null;
  erreur: string | null;
  /** Faux : le message est parti, la commande n'a pas pu être marquée envoyée. */
  marquee_envoyee: boolean;
};

export type ContactFournisseur = {
  id: string;
  nom: string;
  prenom: string | null;
  fonction: string | null;
  email: string | null;
  telephone: string | null;
};

export type FicheFournisseur = Fournisseur & { contacts: ContactFournisseur[] };

/**
 * Les conditions commerciales d'un fournisseur (ticket #76, ADR-0056) : ce que LAB a obtenu
 * pour une période de validité. `etat` se lit en toutes lettres : « À valider » n'alimente
 * rien ; les trois autres sont des conditions validées, selon la date du jour.
 */
export type CumulCommandes = "CUMUL_ACCEPTE" | "COMMANDE_UNIQUE_EXIGEE";
export type EtatConditions = "A_VALIDER" | "EN_VIGUEUR" | "A_VENIR" | "PERIMEE";

export type PieceConditions = {
  id: string;
  nom_fichier_origine: string;
  type_mime: string;
  taille_octets: number;
  cree_le: string;
  depose_par: string | null;
};

export type ConditionsCommerciales = {
  id: string;
  fournisseur_id: string;
  valide_du: string;
  valide_au: string | null;
  seuil_franco_ht: number | null;
  frais_sous_seuil_ht: number | null;
  conditions_livraison: string | null;
  cumul_commandes: CumulCommandes;
  etat: EtatConditions;
  statut: "A_VALIDER" | "VALIDEE";
  valide_par: string | null;
  valide_le: string | null;
  cree_par: string | null;
  cree_le: string;
  pieces: PieceConditions[];
};

/** Les versions, la plus récente d'abord, et ce que l'appelant peut en faire. */
export type ConditionsDeLaFiche = {
  conditions: ConditionsCommerciales[];
  peut_ajouter: boolean;
  peut_modifier: boolean;
  peut_valider: boolean;
};

export type SaisieConditions = {
  valide_du: string;
  valide_au: string | null;
  seuil_franco_ht: number | null;
  frais_sous_seuil_ht: number | null;
  conditions_livraison: string | null;
  cumul_commandes: CumulCommandes;
};

export type EvenementConditions = {
  id: string;
  condition_id: string;
  action: string;
  auteur: string | null;
  cree_le: string;
  donnees: Record<string, unknown>;
};

/** Une fiche préremplie par l'annuaire des entreprises (ticket #13) : rien n'est écrit. */
export type PropositionAnnuaire = {
  nom: string;
  siren: string;
  siret: string | null;
  adresse_ligne1: string | null;
  code_postal: string | null;
  ville: string | null;
};

/** Une fiche existante qui ressemble à celle qu'on crée : un avertissement, pas un refus. */
export type RessemblanceFournisseur = { id: string; nom: string; motifs: string[] };

/**
 * La commande fournisseur (LOT 2, ticket #14), rattachée à une commande client ;
 * chaque ligne désigne un lot, ou aucun (ADR-0001). Sans la permission de voir
 * les montants, prix et montant sont nuls — l'absence ne dit pas « zéro ».
 */
export type StatutCommandeFournisseur =
  | "BROUILLON"
  | "ENVOYEE"
  | "CONFIRMEE"
  | "RECEPTION_PARTIELLE"
  | "RECEPTIONNEE"
  | "CLOTUREE_PARTIELLE"
  | "ANNULEE";

export type GesteCommandeFournisseur = "ENVOI" | "ANNULATION";

/**
 * Une commande client que sert une commande fournisseur, et que l'appelant voit
 * (ticket #37) ; `intervenable` : il en écrit les lignes.
 */
export type CommandeClientServie = {
  id: string;
  numero_commande_client: string;
  intervenable: boolean;
};

export type CommandeFournisseur = {
  id: string;
  /** Ticket #55 : `CF000123`, posé par la base dès le brouillon. */
  numero: string;
  fournisseur: { id: string; nom: string; statut: StatutFournisseur };
  /** Celles que l'appelant voit ; celles d'un autre propriétaire ne se nomment pas. */
  commandes_clients: CommandeClientServie[];
  reference_fournisseur: string | null;
  date_commande: string;
  /** Ticket #71 : la date demandée au fournisseur, qu'aucun AR n'écrase ; `null` : non connue. */
  date_demandee: string | null;
  /** La date courante : la demandée, jusqu'au premier AR qui annonce autre chose. */
  date_prevue: string | null;
  montant_ht: number | null;
  statut: StatutCommandeFournisseur;
  ar_recu_le: string | null;
  nombre_lignes: number;
  /** Ticket #16 : les problèmes fournisseur qui appellent encore une suite. */
  nombre_problemes_ouverts: number;
};

export type TypeProblemeFournisseur =
  | "RETARD"
  | "CASSE"
  | "ERREUR_REFERENCE"
  | "MANQUANT"
  | "LITIGE_TRANSPORT"
  | "ECART_PRIX"
  | "SURPLUS"
  | "AUTRE";

/**
 * Un problème fournisseur (ticket #16) : il touche la commande, ou l'une de ses
 * lignes et donc son lot. Il ne se supprime pas ; résolu, il garde sa résolution.
 */
export type ProblemeFournisseur = {
  id: string;
  type: TypeProblemeFournisseur;
  description: string;
  ligne_commande_fournisseur_id: string | null;
  designation_ligne: string | null;
  code_lot: string | null;
  /** Ticket #22 : la réception logistique où il a été constaté. */
  reception_logistique_id: string | null;
  signale_le: string;
  resolu_le: string | null;
  resolution: string | null;
  /**
   * Ticket #23 : une non-conformité constatée à la réception dit sa quantité et
   * se tranche par une décision ; un autre problème a `null`.
   */
  non_conformite: boolean;
  quantite_non_conforme: number | null;
  unite: string | null;
  decision: DecisionNonConformite | null;
  /** Ouvert, un surplus se solde par un avoir, que la base demande. */
  avoir_possible: boolean;
  resolu_par: string | null;
  /** La demande d'avoir née de la résolution ; `null` sans les montants. */
  montant_avoir_ht: number | null;
  /**
   * Ticket #65 (décision 60) : la dernière réouverture, qui, quand, pourquoi ;
   * la décision qu'elle a défaite est au journal.
   */
  rouvert_le: string | null;
  rouvert_par: string | null;
  motif_reouverture: string | null;
};

export type DecisionNonConformite = "ACCEPTEE" | "ACCEPTEE_AVEC_REMISE" | "MAINTENUE";

export type MotifNonConformite = "CASSE" | "ERREUR_REFERENCE" | "AUTRE";

/** Un fichier d'une réception : il s'ouvre, ou reste fermé à qui n'en voit qu'une partie. */
export type PieceReception = {
  id: string;
  nom_fichier_origine: string;
  lisible: boolean;
};

export type LigneReception = {
  /** La ligne de la réception, que désigne une photo (décision 74). */
  id: string;
  ligne_commande_fournisseur_id: string;
  designation: string;
  numero_commande_client: string;
  code_lot: string | null;
  unite: string;
  quantite_recue: number;
  quantite_non_conforme: number;
  motif_non_conformite: MotifNonConformite | null;
  commentaire: string | null;
  /** Ticket #23 : tranchée, sa non-conformité ne se corrige plus. */
  non_conformite_tranchee: boolean;
  /** Ticket #24 : ce que le BL annonçait, quand la réception vient d'un BL lu. */
  quantite_annoncee: number | null;
  /** Décision 74 : les photos qui la désignent. */
  photos: PieceReception[];
};

/**
 * Une réception logistique (ticket #22, ADR-0046) : ce qui est arrivé, et parmi
 * ce qui est arrivé ce qui n'est pas conforme. Un BL commun relie les réceptions
 * de plusieurs commandes d'un même fournisseur.
 */
export type ReceptionLogistique = {
  id: string;
  commande_fournisseur_id: string;
  date_reception: string;
  receptionne_par: string;
  commentaire: string | null;
  numero_bl: string | null;
  bl: PieceReception | null;
  bl_commun: boolean;
  photos: PieceReception[];
  lignes: LigneReception[];
  /** Ticket #23 : l'appelant peut la corriger. */
  corrigeable: boolean;
};

/** Ticket #23 : l'état voulu des lignes corrigées, le commentaire, le motif. */
export type CorrectionReception = {
  motif: string;
  commentaire: string | null;
  lignes: {
    ligne_commande_fournisseur_id: string;
    quantite_recue: number;
    quantite_non_conforme: number;
    motif_non_conformite: MotifNonConformite | null;
    commentaire: string | null;
  }[];
};

/** Une commande partie qui attend encore une réception (ticket #22). */
export type CommandeAReceptionner = {
  id: string;
  fournisseur: { id: string; nom: string };
  reference_fournisseur: string | null;
  date_commande: string;
  date_prevue: string | null;
  statut: string;
  commandes_clients: { numero_commande_client: string }[];
  lignes_attendues: number;
};

export type SaisieReception = {
  parts: {
    commande_fournisseur_id: string;
    lignes: {
      ligne_commande_fournisseur_id: string;
      quantite_recue: number;
      quantite_non_conforme: number;
      motif_non_conformite: MotifNonConformite | null;
      commentaire: string | null;
    }[];
    /** Celui de cette commande : sur un BL commun, l'autre chargé d'affaires ne le lit pas. */
    commentaire: string | null;
  }[];
  numero_bl: string | null;
};

/** Ticket #24 : un BL fournisseur déposé, qui attend sa validation. */
export type ImportBl = {
  id: string;
  type_attendu: string;
  statut: string;
  importe_le: string;
  document_id: string | null;
  receptions_logistiques: { id: string; commande_fournisseur_id: string }[];
};

/**
 * Ticket #24 : ce que LABGEST propose de constater d'un BL lu, contre les seules
 * lignes que l'appelant peut constater (`rapprocher_bl`).
 */
export type PropositionReception = {
  import_id: string;
  statut: string;
  document_id: string | null;
  numero_bl: string | null;
  commandes_fournisseurs: string[];
  lignes: {
    ligne_commande_fournisseur_id: string;
    commande_fournisseur_id: string;
    quantite_annoncee: number;
    quantite_proposee: number;
  }[];
  non_rapprochees: { reference: string | null; designation: string; quantite: number }[];
  manquants: { ligne_commande_fournisseur_id: string; quantite: number }[];
};

/** Ticket #24 : la réception d'un BL lu, telle que la logistique la valide. */
export type SaisieBl = {
  numero_bl: string | null;
  parts: (Omit<SaisieReception["parts"][number], "lignes"> & {
    lignes: (SaisieReception["parts"][number]["lignes"][number] & {
      quantite_annoncee: number | null;
      manquant_confirme: boolean;
    })[];
  })[];
};

export type LigneCommandeFournisseur = {
  id: string;
  commande_client_id: string;
  numero_commande_client: string;
  lot_id: string | null;
  code_lot: string | null;
  designation: string;
  reference: string | null;
  quantite_commandee: number;
  unite: string;
  prix_unitaire_ht: number | null;
  quantite_recue: number;
  /** ADR-0045 : ce qui est annulé, et ce qui reste à recevoir. */
  quantite_annulee: number;
  reliquat: number;
  /** Ajoutée après l'envoi : pourquoi. */
  motif_ajout: string | null;
  /**
   * Ticket #39 : le prix d'un écart de prix accepté ; nul quand le prix
   * commandé est retenu, ou sans « Consulter les montants ».
   */
  prix_retenu_ht: number | null;
};

/** Une ligne qui en remplace une autre, ou qu'une autre remplace, et chez qui. */
export type LigneLiee = {
  id: string;
  designation: string;
  reference: string | null;
  commande_fournisseur_id: string;
  fournisseur: string;
};

/**
 * Ticket #47 : sur la ligne qui en remplace une autre, les deux prix, l'écart
 * par unité et sur ce qu'elle commande, et la marge prévisionnelle sur achats
 * de sa commande client au prix remplacé puis au prix nouveau. Absent sans
 * « Consulter les montants » ; chaque valeur nulle là où un prix manque, les marges
 * nulles sans prix de vente, et `marge_incomplete` quand le coût ne compte pas tout.
 */
export type Remplacement = {
  prix_remplace_ht: number | null;
  prix_ht: number | null;
  ecart_ht: number | null;
  incidence_ht: number | null;
  marge_avant_ht: number | null;
  marge_apres_ht: number | null;
  marge_incomplete: boolean;
};

export type GesteLigneFournisseur = "ANNULATION_RELIQUAT" | "COMMANDE_AILLEURS";

/** Une ligne de la fiche : ce que l'appelant y fait, et son histoire (ticket #38). */
export type LigneDeLaFicheFournisseur = LigneCommandeFournisseur & {
  intervenable: boolean;
  gestes: GesteLigneFournisseur[];
  annulations: { quantite: number; motif: string; annule_le: string }[];
  remplace: LigneLiee | null;
  remplacee_par: LigneLiee[];
  remplacement: Remplacement | null;
};

/** Ce qu'un AR dit d'une ligne ; sans ligne de commande, un article ajouté. */
export type LigneAr = {
  id: string;
  ligne_commande_fournisseur_id: string | null;
  designation: string;
  reference: string | null;
  quantite: number | null;
  prix_unitaire_ht: number | null;
};

export type NatureEcart = "REFERENCE" | "QUANTITE" | "AJOUT" | "ABSENCE" | "FRAIS_AJOUTES";

/** Des frais que l'AR annonce ; sans frais désignés, des frais ajoutés (ticket #39). */
export type FraisAr = {
  id: string;
  frais_commande_fournisseur_id: string | null;
  nature: NatureFrais;
  libelle: string;
  montant_ht: number;
};

/**
 * Ce que l'AR courant dit autrement que la commande telle qu'elle est, sans
 * circuit de validation (ticket #17, décision 20). L'écart de prix est à part
 * (`EcartPrix`) ; des frais ajoutés n'arrivent qu'avec les montants.
 */
export type EcartAr = {
  nature: NatureEcart;
  ligne_commande_fournisseur_id: string | null;
  designation: string;
  commande: string | null;
  ar: string | null;
};

/** Un AR (ADR-0039) : un fait daté, qui n'écrit que lui-même. */
export type AccuseReception = {
  id: string;
  date_ar: string;
  date_annoncee: string | null;
  reference_fournisseur: string | null;
  enregistre_le: string;
  /** L'import OCR dont l'AR vient, quand il a été déposé en fichier (ticket #18). */
  import_ocr_id: string | null;
  /** Son fichier, qui porte les prix du fournisseur : absent sans `CONSULTER_MONTANTS`. */
  document_id: string | null;
  /**
   * Ticket #41 : un document commun à plusieurs commandes fournisseurs ; son
   * fichier s'ouvre aux chargés d'affaires concernés (ticket #48 ; `document_id` absent sinon).
   */
  document_commun: boolean;
  /** Relevé dans la boîte des AR : son courriel (ticket #19). */
  courriel: CourrielReleve | null;
  /**
   * Ticket #64 : l'appelant peut corriger son rattachement — il valide les AR et agit sur
   * la commande, l'AR vient d'un courriel et n'est pas d'un document commun.
   */
  rattachement_corrigeable: boolean;
  lignes: LigneAr[];
  /** Ticket #39 : nul sans « Consulter les montants ». */
  frais: FraisAr[] | null;
  /** L'AR courant, le plus récent : seuls ses écarts se lisent (ticket #39). */
  courant: boolean;
  ecarts: EcartAr[];
};

/** `DETACHE` (ticket #64) : l'AR dont il venait a été détaché de la commande. */
export type StatutEcartPrix =
  | "A_VALIDER"
  | "AUTORISE"
  | "ACCEPTE"
  | "CONTESTE"
  | "RESORBE"
  | "DETACHE";

/** Ticket #46 : ce que l'appelant peut faire d'un écart. */
export type GesteEcartPrix = "DECIDER" | "AUTORISER" | "CONFIRMER";

/**
 * La marge prévisionnelle sur achats d'une commande client, avant et après. `null` sans
 * prix de vente ; `incomplete` : le coût ne compte pas tout (lignes sans prix, frais non
 * répartis), la marge est partielle, l'écran le dit en toutes lettres.
 */
export type MargeAvantApres = {
  commande_client_id: string;
  numero_commande_client: string;
  avant_ht: number | null;
  apres_ht: number | null;
  incomplete: boolean;
};

/** Ticket #72 : ce que l'acceptation d'un écart de ligne dit du prix. */
export type NaturePrix = "NOUVEAU_TARIF" | "PRIX_DE_CETTE_COMMANDE";

/**
 * Ticket #39 : un écart de prix fournisseur, sur une ligne ou des frais.
 * `geste` (ticket #46) : l'appelant le décide en une étape, l'autorise, ou le
 * confirme ; sinon, `attente` dit pourquoi en toutes lettres.
 */
export type EcartPrix = {
  id: string;
  ligne_commande_fournisseur_id: string | null;
  frais_commande_fournisseur_id: string | null;
  objet: string;
  /** Ticket #75 : la nature des frais ; `null` pour l'écart d'une ligne. */
  nature_frais: NatureFrais | null;
  numero_commande_client: string | null;
  fournisseur: string;
  commande_fournisseur_id: string;
  statut: StatutEcartPrix;
  prix_initial_ht: number | null;
  prix_precedent_ht: number | null;
  prix_ar_ht: number;
  pourcentage: number | null;
  quantite: number | null;
  incidence_ht: number;
  /** Nulle pour des frais ajoutés après l'envoi (ticket #66) : ils n'ont pas d'AR. */
  date_ar: string | null;
  cree_le: string;
  resorbe_par_date_ar: string | null;
  decideur: string | null;
  decide_le: string | null;
  motif: string | null;
  plafond_applique: number | null;
  probleme_id: string | null;
  autorise_par: string | null;
  autorise_le: string | null;
  charge_affaires: string | null;
  /** Ticket #50 : accepté d'office dans la tolérance sur les frais, sans décideur. */
  tolerance_appliquee: number | null;
  /** Ticket #72 : dit en acceptant un écart de ligne ; vide pour des frais et pour un écart plus ancien. */
  nature_prix: NaturePrix | null;
  geste: GesteEcartPrix | null;
  /** Ticket #59 : qui agit sur la commande conteste un écart qui attend, sans permission de valider. */
  contestable: boolean;
  attente: string | null;
  marges: MargeAvantApres[];
};

/**
 * Ticket #51 : une hausse de frais acceptée, pour information. Sans `decideur`,
 * la tolérance sur les frais l'a acceptée d'office. `commandes_clients` : celles
 * que l'appelant voit, avec leur part de la hausse et leur marge prévisionnelle
 * sur achats sans elle et aujourd'hui ; `null` quand un prix manque ou sans prix de vente,
 * `marge_incomplete` quand le coût de la commande client ne compte pas tout.
 */
export type HausseFrais = {
  ecart_id: string;
  commande_fournisseur_id: string;
  objet: string;
  nature_frais: NatureFrais | null;
  fournisseur: string;
  montant_precedent_ht: number | null;
  montant_ar_ht: number;
  hausse_ht: number;
  acceptee_le: string;
  decideur: string | null;
  tolerance_appliquee: number | null;
  commandes_clients: {
    commande_client_id: string;
    numero_commande_client: string;
    part_hausse_ht: number | null;
    marge_sans_hausse_ht: number | null;
    marge_ht: number | null;
    marge_incomplete: boolean;
  }[];
};

/** Le courriel d'où vient un AR relevé dans la boîte dédiée (ticket #19). */
export type CourrielReleve = { expediteur: string; objet: string; recu_le: string };

/** Ticket #20 : une pièce relevée que LABGEST n'a rapprochée d'aucune commande. */
export type EtatDocumentEntrant = "A_TRAITER" | "RATTACHE" | "ECARTE";
export type DocumentEntrant = {
  id: string;
  etat: EtatDocumentEntrant;
  courriel: CourrielReleve;
  document_id: string | null;
  nom_fichier: string | null;
  motif_ecart: string | null;
  traite_le: string | null;
  traite_par: string | null;
  /** Rattaché : l'import d'AR qu'il est devenu, et sa commande fournisseur. */
  import_ocr_id: string | null;
  commande_fournisseur_id: string | null;
};

/** Un AR déposé en fichier sur la commande, qui attend sa validation (ticket #18). */
export type ArAValider = {
  id: string;
  importe_le: string;
  document_id: string | null;
  /** Le profil de l'appelant valide les AR, et il agit sur la commande. */
  validable: boolean;
  /** Relevé dans la boîte des AR : son courriel. Déposé à la main : `null`. */
  courriel: CourrielReleve | null;
  /** Ticket #64 : l'AR relevé a pu être rattaché à la mauvaise commande ; il se corrige. */
  rattachement_corrigeable: boolean;
};

/** Ticket #19 : un AR relevé dont cette commande est l'une des candidates. */
export type ArARattacher = {
  id: string;
  importe_le: string;
  document_id: string | null;
  courriel: CourrielReleve | null;
  /** L'appelant peut désigner cette commande comme la sienne. */
  choisissable: boolean;
};

/**
 * Ticket #41 : un document commun du même fournisseur, en partie rattaché, qui
 * couvre peut-être cette commande. Les commandes qui l'ont reçu ne se nomment
 * pas ; son fichier s'ouvre si LABGEST y a reconnu cette commande (ticket #48).
 */
export type ArCommunEnPartie = {
  id: string;
  importe_le: string;
  document_id: string | null;
  courriel: CourrielReleve | null;
  commandes_rattachees: number;
  validable: boolean;
};

/** Ticket #41 : une autre commande du fournisseur, qui attend un AR, où l'on agit. */
export type CommandeAttendantUnAr = {
  id: string;
  date_commande: string;
  reference_fournisseur: string | null;
  numeros_commandes_clients: string[];
};

/** Ticket #41 : une commande que nomme un document commun en partie rattaché. */
export type CommandeDuDocument = {
  commande_fournisseur_id: string;
  date_commande: string;
  reference_fournisseur: string | null;
  numeros_commandes_clients: string[];
};

/** Ticket #41 : un document commun « en partie rattaché », dans la file. */
export type DocumentEnPartie = {
  import_ocr_id: string;
  fournisseur: string;
  importe_le: string;
  courriel: CourrielReleve | null;
  document_id: string | null;
  nom_fichier: string | null;
  rattachees: CommandeDuDocument[];
  a_rattacher: CommandeDuDocument[];
};

/**
 * `intervenable` dit la commande client (ADR-0032), `gestes` ce que
 * l'avancement permet encore : l'écran les combine avec la permission.
 */
/** Ticket #71 : ce qu'un AR annonce ; le dernier qui annonce une date est la courante. */
export type DateAnnoncee = {
  ar_id: string;
  date_ar: string;
  date_annoncee: string;
  courante: boolean;
};

/** Ticket #78 : un brouillon que quelqu'un d'autre prépare chez le même fournisseur. */
export type BrouillonDUnAutre = {
  id: string;
  numero: string;
  prepare_par: string | null;
  commandes_clients: string[];
  nombre_lignes: number;
  /** Vide quand une des lignes visibles n'a pas de prix. */
  montant_ht: number | null;
};

export type EtatFranco =
  | "SOUS_LE_FRANCO"
  | "FRANCO_ATTEINT"
  | "MONTANT_INCOMPLET"
  | "SANS_FRANCO"
  | "CONDITIONS_NON_RENSEIGNEES"
  | "SANS_OBJET";

/** Ticket #78 : le contrôle informatif du franco. Il informe, il ne bloque jamais. */
export type ControleFranco = {
  etat: EtatFranco;
  message: string;
  seuil_franco_ht: number | null;
  montant_ht: number | null;
  /** Toujours positif : l'état dit le sens. */
  ecart_ht: number | null;
  frais_prevus_ht: number | null;
  conditions_livraison: string | null;
  cumul_commandes: string | null;
  valide_du: string | null;
  valide_au: string | null;
  lignes_sans_prix: number;
  /** Une part d'une commande mêlée : le montant est celui des lignes de l'appelant. */
  part_partielle: boolean;
  brouillons_autres: BrouillonDUnAutre[];
};

export type FicheCommandeFournisseur = CommandeFournisseur & {
  /** La date confirmée fournisseur : celle du dernier AR qui en annonce une. */
  date_annoncee: string | null;
  /** Jours entre la demandée et la confirmée : positif, la confirmée est plus tard. */
  ecart_annonce_jours: number | null;
  /** Décision 124 b : l'écart en semaines ISO, signé (positif : la confirmée est plus tard). */
  ecart_annonce_semaines: number | null;
  /** AR par AR, le dernier d'abord. */
  dates_annoncees: DateAnnoncee[];
  // Ticket #80 : les tentatives d'envoi par courriel, la dernière d'abord.
  envois_courriel: EnvoiCourriel[];
  /** Les lignes des commandes client que l'appelant voit ; il écrit les `intervenable`. */
  lignes: LigneDeLaFicheFournisseur[];
  /**
   * Ticket #37 : après un transfert, les commandes client de l'autre propriétaire,
   * réduites au nombre de leurs lignes.
   */
  autres_commandes_clients: { nombre_lignes: number }[];
  /** Le dernier d'abord (ticket #17). */
  accuses_reception: AccuseReception[];
  /** Le plus ancien d'abord. */
  ar_a_valider: ArAValider[];
  /** Les AR relevés qui pourraient être ceux de cette commande (ticket #19). */
  ar_a_rattacher: ArARattacher[];
  /** Ticket #41 : les documents communs en partie rattachés qui la couvrent peut-être. */
  ar_communs_en_partie: ArCommunEnPartie[];
  /** Ticket #41 : les autres commandes du fournisseur qu'un document commun peut couvrir. */
  commandes_attendant_un_ar: CommandeAttendantUnAr[];
  /** Ouverts d'abord ; les résolus restent, avec leur résolution. */
  problemes: ProblemeFournisseur[];
  intervenable: boolean;
  gestes: GesteCommandeFournisseur[];
  /** Envoyée, ses lignes ne se corrigent plus (ADR-0045). */
  corrigeable: boolean;
  /** Annulée, son en-tête non plus. */
  en_tete_corrigeable: boolean;
  /** Envoyée, corriger l'en-tête demande un motif neuf (ticket #58). */
  correction_motivee: boolean;
  /** Librement en brouillon, avec un motif après l'envoi, plus du tout ensuite. */
  ajout_ligne: "LIBRE" | "MOTIVE" | null;
  /** Elle a reçu : son annulation porte sur le reliquat de chaque ligne, avec un motif. */
  annulation_motivee: boolean;
  /**
   * Ticket #56 : nul tant que la commande n'est pas annulée. « BLOC » : un geste sur la
   * commande entière ; « RELIQUATS » : tout son reliquat l'a été, ligne par ligne. Les motifs
   * sont ceux du geste, ou ceux des reliquats que l'appelant voit.
   */
  annulation_de_la_commande: { chemin: "BLOC" | "RELIQUATS"; motifs: string[] } | null;
  /** Ticket #40 : nul sans « Consulter les montants ». */
  frais: FraisFournisseur[] | null;
  /** Porter des frais : un geste sur l'ensemble, tant que la commande vit. */
  frais_ajoutables: boolean;
  /** Les retirer : en brouillon seulement. */
  frais_retirables: boolean;
  /** Ticket #39 : à valider d'abord ; nul sans « Consulter les montants ». */
  ecarts_prix: EcartPrix[] | null;
  /** Ticket #44 : celles des commandes client que l'appelant voit, les plus récentes d'abord. */
  demandes_avoir: DemandeAvoir[];
  /** L'appelant peut en demander une, pour l'une des commandes client où il agit. */
  demande_avoir_possible: boolean;
  /** Ticket #22 : les plus récentes d'abord, réduites aux lignes que l'appelant voit. */
  receptions: ReceptionLogistique[];
  /** L'appelant peut constater une réception sur cette commande. */
  receptionnable: boolean;
};

/** Ticket #47 : reçue et validée ne s'atteignent qu'au LOT 8, par la pièce d'avoir. */
export type StatutDemandeAvoir =
  | "EN_NEGOCIATION"
  | "ATTENDUE"
  | "RECUE"
  | "VALIDEE"
  | "ABANDONNEE";

/**
 * Une demande d'avoir fournisseur (ticket #44) : un montant que LAB attend du
 * fournisseur, pour une commande client que sert la commande fournisseur.
 * Montant nul sans « Consulter les montants » ; elle ne touche pas le coût
 * d'achat prévisionnel.
 */
export type DemandeAvoir = {
  id: string;
  commande_fournisseur_id: string;
  fournisseur: string;
  reference_fournisseur: string | null;
  commande_client_id: string;
  numero_commande_client: string;
  ligne_commande_fournisseur_id: string | null;
  designation_ligne: string | null;
  probleme_commande_fournisseur_id: string | null;
  montant_ht: number | null;
  motif: string;
  statut: StatutDemandeAvoir;
  demandee_le: string;
  motif_abandon: string | null;
  abandonnee_le: string | null;
  /** Ticket #49 : la dernière correction d'une demande attendue, et pourquoi. */
  motif_correction: string | null;
  corrigee_le: string | null;
  /** L'appelant peut l'abandonner. */
  abandonnable: boolean;
  /** En négociation, l'appelant dit que le fournisseur l'accepte. */
  acceptable: boolean;
  /** En négociation ou attendue, l'appelant en corrige le montant. */
  montant_corrigeable: boolean;
  /** Attendue : une correction dit son motif (ticket #49). */
  motif_correction_exige: boolean;
};

export type SaisieDemandeAvoir = {
  ligne_commande_fournisseur_id: string | null;
  commande_client_id: string | null;
  probleme_commande_fournisseur_id: string | null;
  montant_ht: number;
  motif: string;
  /** Le fournisseur a déjà accepté : elle naît attendue. */
  attendue: boolean;
};

export type NatureFrais = "TRANSPORT" | "FACTURATION" | "EMBALLAGE" | "AUTRE";

/**
 * Des frais de la commande fournisseur entière (ticket #40) : leur montant est
 * le sien ; chaque part, celle d'une commande client que l'appelant voit, en
 * euros, nulle tant qu'une ligne commandée attend son prix.
 */
export type FraisFournisseur = {
  id: string;
  nature: NatureFrais;
  libelle: string;
  montant_ht: number;
  /** Ticket #39 : le montant d'un écart de prix accepté, qui se ventile ; nul sinon. */
  montant_retenu_ht: number | null;
  /**
   * Ticket #66 (décision 61) : des frais ajoutés après l'envoi naissent à un montant nul ;
   * `montant_demande_ht` est celui qu'on a saisi, `motif_ajout` la raison, `statut_ecart` ce
   * que devient leur écart de frais. Nuls pour des frais portés en brouillon.
   */
  montant_demande_ht: number | null;
  motif_ajout: string | null;
  statut_ecart: StatutEcartPrix | null;
  parts: { commande_client_id: string; numero_commande_client: string; part_ht: number | null }[];
};

/**
 * Ticket #40 : ce que les achats d'une commande client doivent coûter d'après
 * ses commandes fournisseurs. Ticket #68 : le coût compte ce qui est connu et
 * dit ce qui manque ; la marge n'est nulle que sans prix de vente. Les prévus
 * sont les brouillons et les envoyées (le coût lui-même), les engagés les
 * envoyées seules ; les frais en attente de décision ne comptent pas.
 */
export type AchatsPrevisionnels = {
  achats_ht: number;
  frais_ht: number;
  cout_achat_previsionnel_ht: number;
  marge_previsionnelle_achats_ht: number | null;
  achats_prevus_ht: number;
  achats_engages_ht: number;
  frais_en_attente_ht: number;
  lignes_sans_prix: number;
  commandes_frais_non_repartis: number;
  frais_en_attente_non_repartis: number;
  incomplet: boolean;
};

/**
 * Une ligne fournisseur vue depuis son lot (ticket #15) : ce qu'elle est, chez
 * qui, et où en est sa commande. Sans `CONSULTER_MONTANTS`, le prix est nul.
 */
export type LigneFournisseurDuLot = LigneCommandeFournisseur & {
  commande_fournisseur_id: string;
  fournisseur: { id: string; nom: string; statut: StatutFournisseur };
  reference_fournisseur: string | null;
  date_commande: string;
  date_prevue: string | null;
  statut_commande: StatutCommandeFournisseur;
  /** Ceux de la ligne, et ceux de sa commande entière (sans `designation_ligne`). */
  problemes_ouverts: ProblemeFournisseur[];
};

/** Une commande client qu'une ligne fournisseur peut servir, avec ses lots (ticket #37). */
export type CommandeClientProposee = {
  id: string;
  numero_commande_client: string;
  lots: { id: string; code_lot: string; intitule: string }[];
};

/** Un collègue tel qu'on le nomme à l'écran — et rien de plus. */
export type Personne = { id: string; nom: string; prenom: string };

/**
 * L'affaire : le sommet du dossier (ADR-0028). Elle regroupe les commandes d'un
 * client ; la commande reste la référence commerciale. Elle ne porte aucune
 * personne : le propriétaire est sur chaque commande (ADR-0032).
 */
export type Affaire = {
  id: string;
  numero: string;
  client_id: string;
  cree_le: string;
};

export type CommandeAbregee = {
  id: string;
  numero_commande_client: string;
  affaire_id: string;
  numero_affaire: string;
  date_commande: string;
  statut: string;
  montant_ht: number | null;
  nombre_lots: number;
};

// `intervenable` : une de ses commandes s'ouvre à l'appelant en intervention,
// ce que sa correction exige (ADR-0032).
export type FicheClient = { client: Client; commandes: CommandeAbregee[]; intervenable: boolean };

/** La fiche d'un lot : sa vue, et si l'appelant peut y agir (ADR-0032). */
export type FicheLot = Lot & { intervenable: boolean };

export type Commande = {
  id: string;
  numero_commande_client: string;
  date_commande: string;
  date_signature: string | null;
  statut: string;
  // `null` quand l'appelant n'a pas `CONSULTER_MONTANTS` : voir un dossier
  // n'est pas voir ce qu'il vaut. C'est le SERVEUR qui décide ; l'écran ne
  // fait que ne pas dessiner de cases vides.
  statut_reglement: string | null;
  montant_ht: number | null;
  tva_10_applicable: boolean | null;
  tva_10_origine: string | null;
  cloturee_definitivement: boolean;
  commentaires: string | null;
  // Ce que la commande porte (ADR-0031).
  categorie: string;
  categorie_libelle: string;
  mode_remise: "EMPORT" | "LIVRAISON";
  avec_pose: boolean;
  adresse_realisation: Adresse | null;
  adresse_facturation: Adresse;
  // Le vendeur d'origine ne change jamais et ne donne aucun droit ; le
  // propriétaire se transfère (ADR-0032).
  charge_affaires_id: string;
  vendeur_origine: Personne;
  proprietaire: Personne;
  affaire: Affaire;
  client: Client;
  nombre_lots: number;
};

export type AffaireAbregee = {
  affaire: Affaire;
  client: Client;
  nombre_commandes: number;
  // Les propriétaires des commandes que l'on voit : une affaire peut en
  // regrouper de plusieurs chargés d'affaires.
  proprietaires: Personne[];
};

export type CommandeDeLAffaire = { commande: Commande; lots: Lot[] };

export type FicheAffaire = {
  affaire: Affaire;
  client: Client;
  commandes: CommandeDeLAffaire[];
};

export type FicheCommande = {
  commande: Commande;
  /** Nul sans « Consulter les montants » (ticket #40). */
  achats_previsionnels: AchatsPrevisionnels | null;
  /** Ticket #44 : ses demandes d'avoir fournisseur, les plus récentes d'abord. */
  demandes_avoir: DemandeAvoir[];
  lots: Lot[];
  exigences_requises: number;
  documents: number;
  cloture_proposable: boolean;
  statuts_possibles: string[];
  // Ce que l'appelant peut faire de cette commande (ADR-0032). L'écran montre
  // ou cache les gestes d'après ces booléens ; le serveur refuse quoi qu'il en
  // soit. `intervenable` faux : on la consulte sans pouvoir y agir, et
  // `sans_intervention` dit pourquoi, dans la phrase du serveur.
  intervenable: boolean;
  sans_intervention: string | null;
  regroupable: boolean;
  habilitable: boolean;
  transferable: boolean;
  // À qui peut transférer seulement (ADR-0043) : nul pour tout autre, et
  // quand la commande n'est pas transférée pour un temps.
  transfert_temporaire: { fin: string; revient_a: Personne } | null;
  // Au même : le transfert temporaire échu dont la commande n'a pas pu revenir,
  // jusqu'au transfert suivant (ADR-0043, décision 15).
  retour_impossible: { fin: string; revient_a: Personne } | null;
};

/**
 * De quoi reconnaître un client hors de son périmètre, et rien de plus
 * (ADR-0032) : ni adresse, ni courriel, ni commandes.
 */
export type ClientReconnu = {
  id: string;
  type: string;
  designation: string;
  code_postal: string | null;
  ville: string | null;
  fin_telephone: string | null;
};

/** Le numéro d'une affaire, pour y rattacher ou regrouper sans la voir. */
export type AffaireReconnue = { id: string; numero: string; cree_le: string };

export type NiveauAcces = "CONSULTATION" | "INTERVENTION";

export type Habilitation = {
  id: string;
  beneficiaire_id: string;
  beneficiaire_nom: string;
  commande_client_id: string;
  numero_commande_client: string;
  niveau: NiveauAcces;
  du: string;
  // Nul : sans fin.
  au: string | null;
  en_cours: boolean;
  motif: string | null;
  cree_par_id: string;
  cree_le: string;
  revoquee_le: string | null;
  revoquee_par_id: string | null;
  motif_revocation: string | null;
  revocable: boolean;
  // Révocable par l'appelant (une révoquée ne l'est plus) et pas échue : son
  // niveau et sa fin se modifient.
  modifiable: boolean;
};

/** L'état voulu, entier : `au` nul retire la fin. */
export type ModificationHabilitation = {
  niveau: NiveauAcces;
  au: string | null;
  motif: string | null;
};

/** Ce qu'un humain saisit d'après le BDC — et ce que l'OCR proposera un jour. */
export type LotSaisi = {
  intitule: string;
  categorie: string;
  montant_ht_quote_part?: string | null;
  responsable_operationnel_id?: string | null;
  commentaires?: string | null;
};

export type CommandeSaisie = {
  numero_commande_client: string;
  date_commande: string;
  charge_affaires_id: string;
  client_id?: string | null;
  client_nouveau?: Record<string, string | null> | null;
  montant_ht?: string | null;
  date_signature?: string | null;
  tva_10_applicable?: boolean;
  commentaires?: string | null;
  categorie: string | null;
  mode_remise: "EMPORT" | "LIVRAISON";
  avec_pose: boolean;
  adresse_realisation: Adresse | null;
  adresse_facturation: Adresse;
  // Vide : la commande n'est pas découpée, elle reçoit son lot unique.
  lots: LotSaisi[];
  // Absente : l'affaire naît avec la commande. Présente : le client revient.
  affaire_id?: string | null;
};

/** Ce que rend une liste : ses éléments, et de quoi savoir où l'on en est. */
export type Page<T> = {
  elements: T[];
  total: number;
  limite: number;
  decalage: number;
};

export type Collegue = { id: string; nom: string; prenom: string; profils: ProfilPorte[] };

export type Document = {
  id: string;
  categorie: string;
  nom_fichier_origine: string;
  type_mime: string;
  taille_octets: number;
  hash_sha256: string;
  visible_client: boolean;
  publie_le: string | null;
  cree_le: string;
  lot_id: string | null;
  code_lot: string | null;
  depose_par: string | null;
};

/** Ticket #110 : une photo ou une vidéo d'une fiche, dans l'une de ses zones de médias. */
export type MediaFiche = Document & { zone: string };

/** Où déposer : sur le dossier lui-même, ou sur l'un de ses lots. */
export type CibleDocument = { commande_client_id: string } | { lot_id: string };

export type Trouvaille = { type: string; id: string; titre: string; detail: string | null };

export type Resultats = {
  terme: string;
  affaires: Trouvaille[];
  commandes: Trouvaille[];
  lots: Trouvaille[];
  clients: Trouvaille[];
  total: number;
};

/** Une valeur d'un référentiel administrable : le code est stable, le reste se règle. */
export type ValeurReferentiel = {
  id: string;
  code: string;
  libelle: string;
  ordre: number;
  actif: boolean;
  maj_le: string;
  /** Les seuls types de documents en portent (ADR-0062). */
  reglages?: ReglagesTypeDocument | null;
};

export type Referentiel = { nom: string; libelle: string; valeurs: ValeurReferentiel[] };

/* Ticket #106 (ADR-0060) : les modèles de document et leurs versions. La définition
   se range côté serveur (`domaine/definition_modele.py`) ; ces types en disent la forme. */
export type TypeChamp =
  | "TEXTE_COURT"
  | "TEXTE_LONG"
  | "NOMBRE"
  | "COTE_MM"
  | "DATE"
  | "OUI_NON"
  | "CHOIX_UNIQUE"
  | "CHOIX_MULTIPLE"
  | "CHAMP_LIE"
  /* Ticket #110 : les photos et vidéos de la fiche, déposées comme documents. */
  | "ZONE_MEDIAS"
  /* Ticket #112 : où la fiche montre ses signatures ; sans valeur ni paramètre. */
  | "EMPLACEMENT_SIGNATURE";

/* Ticket #108 : ce qu'un champ lié reprend du dossier, liste fermée côté serveur. */
export type SourceChampLie =
  | "CLIENT"
  | "ADRESSE_REALISATION"
  | "CODE_LOT"
  | "CHARGE_AFFAIRES"
  | "CATEGORIE";

export type ChampModele = {
  cle: string;
  libelle: string;
  type: TypeChamp;
  obligatoire: boolean;
  parametres: { unite?: string; options?: string[]; source?: SourceChampLie };
};

export type SectionModele = {
  cle: string;
  titre: string;
  /**
   * Ticket #109 : le code d'une permission du catalogue ; qui ne la porte pas ne voit
   * ni n'écrit la section. Absente, la section suit le droit d'ouvrir la fiche.
   */
  cle_visibilite?: string;
  champs: ChampModele[];
};
export type DefinitionModele = { sections: SectionModele[] };
export type StatutVersionModele = "BROUILLON" | "PUBLIEE" | "RETIREE";

export type VersionModele = {
  id: string;
  numero: number;
  statut: StatutVersionModele;
  definition: DefinitionModele;
  cree_le: string;
  cree_par: string | null;
  publiee_le: string | null;
  publiee_par: string | null;
  retiree_le: string | null;
  retiree_par: string | null;
};

export type ModeleDocument = {
  id: string;
  nom: string;
  type_document: string;
  type_libelle: string;
  actif: boolean;
  cree_le: string;
  versions: VersionModele[];
};

export type ModeleDocumentResume = {
  id: string;
  nom: string;
  type_document: string;
  type_libelle: string;
  actif: boolean;
  version_en_usage: number | null;
  brouillon: number | null;
};

/* Ticket #107 (ADR-0060) : les fiches, documents remplis d'après une version publiée.
   Les valeurs se contrôlent côté serveur (`domaine/fiches.py`), clé de champ → valeur. */
export type StatutFiche = "BROUILLON" | "VALIDEE" | "SIGNEE" | "ABANDONNEE";
export type ValeurFiche = string | number | boolean | string[];

export type FicheResume = {
  id: string;
  modele_nom: string;
  version_numero: number;
  type_libelle: string;
  statut: StatutFiche;
  cree_le: string;
  maj_le: string;
  figee_le: string | null;
};

export type Fiche = FicheResume & {
  lot_id: string;
  code_lot: string;
  modele_id: string;
  type_document: string;
  demande: "AUCUNE" | "VALIDATION" | "SIGNATURE";
  definition: DefinitionModele;
  valeurs: Record<string, ValeurFiche>;
  /* Les champs liés (#108) : lus dans le dossier en brouillon, recopiés au figement. */
  valeurs_liees: Record<string, string | null>;
  /* Ticket #110 : les médias des zones que le lecteur voit, dans l'ordre du dépôt. */
  medias: MediaFiche[];
  /* Ticket #111 : le PDF scellé à la validation ; absent pour qui ne voit pas tout. */
  pdf_scelle: Document | null;
  /* Ticket #112 : une qualité par signature attendue, celles qui manquent, et celles posées. */
  signatures_attendues: QualiteSignataire[];
  signatures_manquantes: QualiteSignataire[];
  signatures: SignatureFiche[];
  cree_par: string | null;
  figee_par: string | null;
  abandonnee_le: string | null;
  modifiable: boolean;
  validable: boolean;
  signable: boolean;
};

export type CanalSignature = "SESSION_INTERNE" | "ESPACE_CLIENT" | "LIEN_COURRIEL";

export type SignatureFiche = {
  id: string;
  signataire_nom: string;
  signataire_type: QualiteSignataire;
  signe_le: string;
  canal: CanalSignature;
  /* Qui a présenté l'écran, en session interne. */
  presentee_par: string | null;
};

export type ModeleDisponible = {
  id: string;
  nom: string;
  type_libelle: string;
  version_numero: number;
};

export type QualiteSignataire = "CLIENT" | "COLLABORATEUR" | "INTERVENANT";

/** Ce que LAB règle par type de document : ce qu'il demande, sa publication, sa conservation. */
export type ReglagesTypeDocument = {
  demande: "AUCUNE" | "VALIDATION" | "SIGNATURE";
  /** Une qualité par signature attendue : deux clients, c'est deux fois CLIENT. */
  signataires_attendus: QualiteSignataire[];
  publiable_client: boolean;
  exigible: boolean;
  classe_conservation: "PREUVE_CHANTIER" | "MARKETING_AUTORISE" | "PIECE_PROBANTE";
};

/** Un seuil d'alerte (LAB, 10/09 §05), réglé sous « Gérer les alertes » et tracé. */
export type SeuilAlerte = {
  id: string;
  code: string;
  libelle: string;
  valeur: number;
  unite: "POURCENT" | "JOURS" | "MO";
  actif: boolean;
  /** Faux pour un seuil que rien ne lit encore : il attend le LOT des alertes. */
  en_service: boolean;
  maj_le: string;
};

/** Un référentiel tel que le registre de l'API le nomme, sans ses valeurs. */
export type EntreeRegistre = { nom: string; libelle: string };

/** Les trois familles de périmètre (ADR-0035) ; elles ne se paramètrent pas. */
export type Famille = "AFFECTATIONS" | "SES_COMMANDES" | "TOUT";

/** Ce que l'Administrateur règle d'un profil ; le code, lui, ne change pas. */
export type ReglageProfil = {
  libelle: string;
  famille: Famille;
  permissions: string[];
  /** Vide : le profil valide tous les types de documents (ADR-0025). */
  types_ocr: string[];
  /**
   * Ticket #115 : les types de documents remplis qu'il valide, sous « Valider un
   * document rempli » ; vide, il les valide tous (ADR-0062).
   */
  types_valides: string[];
  /**
   * ADR-0047 : le plafond de « Valider les écarts de prix fournisseur », en % ;
   * `null`, sans plafond.
   */
  plafond_pourcentage: string | null;
  /** Ticket #50 : celui de « Valider les écarts de frais fournisseur ». */
  plafond_frais_pourcentage: string | null;
  /**
   * Ticket #60 (décision 72) : le plafond en euros des écarts sans prix initial,
   * sous les mêmes permissions ; `null`, aucun — le profil ne tranche pas ces
   * écarts, sauf s'il est sans plafond en pourcentage.
   */
  plafond_euros: string | null;
  plafond_frais_euros: string | null;
};

export type ProfilDetaille = ReglageProfil & { code: string; porteurs: number };

/**
 * Ticket #115 : un type du référentiel, pour régler ceux qu'un profil valide. Seul un
 * type actif qui demande une validation se coche ; les autres relisent un réglage posé.
 */
export type TypeValidable = {
  code: string;
  libelle: string;
  actif: boolean;
  demande_validation: boolean;
};

export type PermissionCatalogue = {
  code: string;
  libelle: string;
  ouverte_aux_externes: boolean;
  /** Ticket #109 : une clé de visibilité de section, créée par l'Administrateur. */
  cle_de_visibilite: boolean;
};

export type Nature = "INTERNE" | "EXTERNE";

export type Compte = {
  id: string;
  nom: string;
  prenom: string;
  nature: Nature;
  actif: boolean;
  profils: ProfilPorte[];
};

export type Sante = {
  etat: string;
  version: string;
  environnement: string;
  base: string;
  revision_migration: string | null;
};

import { appeler, televerser } from "@/lib/http";

export { ErreurApi } from "@/lib/http";

export const api = {
  connexion: (email: string, motDePasse: string) =>
    appeler<Identite>("/api/authentification/connexion", {
      methode: "POST",
      corps: { email, mot_de_passe: motDePasse },
    }),
  deconnexion: () => appeler<void>("/api/authentification/deconnexion", { methode: "POST" }),
  moi: (signal?: AbortSignal) => appeler<Identite>("/api/authentification/moi", { signal }),
  sante: (signal?: AbortSignal) => appeler<Sante>("/api/sante", { signal }),

  // --- LOT 1 -----------------------------------------------------------------
  lots: (filtres: FiltresListe = {}, signal?: AbortSignal) =>
    appeler<Page<Lot>>(`/api/lots${parametres(filtres)}`, { signal }),
  lot: (id: string, signal?: AbortSignal) =>
    appeler<FicheLot>(`/api/lots/${id}`, { signal }),
  corrigerLot: (id: string, correction: Record<string, unknown>) =>
    appeler<FicheLot>(`/api/lots/${id}`, { methode: "PATCH", corps: correction }),

  commandes: (filtres: FiltresListe & { statut?: string } = {}, signal?: AbortSignal) =>
    appeler<Page<Commande>>(`/api/commandes${parametres(filtres)}`, { signal }),
  commande: (id: string, signal?: AbortSignal) =>
    appeler<FicheCommande>(`/api/commandes/${id}`, { signal }),
  creerCommande: (saisie: CommandeSaisie) =>
    appeler<FicheCommande>("/api/commandes", { methode: "POST", corps: saisie }),
  corrigerCommande: (id: string, correction: Record<string, unknown>) =>
    appeler<FicheCommande>(`/api/commandes/${id}`, { methode: "PATCH", corps: correction }),
  changerStatutCommande: (id: string, statut: string) =>
    appeler<FicheCommande>(`/api/commandes/${id}/statut`, { methode: "POST", corps: { statut } }),
  ajouterLot: (id: string, lot: LotSaisi) =>
    appeler<FicheCommande>(`/api/commandes/${id}/lots`, { methode: "POST", corps: lot }),
  // `fin` nulle : définitif ; sinon le dernier jour, inclus.
  transferer: (id: string, proprietaire_id: string, fin: string | null, motif: string | null) =>
    appeler<FicheCommande>(`/api/commandes/${id}/proprietaire`, {
      methode: "POST",
      corps: { proprietaire_id, fin, motif },
    }),
  // `fin` nulle : le transfert temporaire devient définitif.
  modifierFinDuTransfert: (id: string, fin: string | null, motif: string | null) =>
    appeler<FicheCommande>(`/api/commandes/${id}/transfert`, {
      methode: "PATCH",
      corps: { fin, motif },
    }),
  // Décision 76 : le transfert pour un temps devient définitif, sa fin et son
  // avertissement s'effacent.
  garderChezLeProprietaireActuel: (id: string, motif: string | null = null) =>
    appeler<FicheCommande>(`/api/commandes/${id}/transfert/garder`, {
      methode: "POST",
      corps: { motif },
    }),

  regrouper: (id: string, affaire_id: string, motif: string | null) =>
    appeler<FicheCommande>(`/api/commandes/${id}/affaire`, {
      methode: "POST",
      corps: { affaire_id, motif },
    }),

  reconnaitreClients: (q: string, signal?: AbortSignal) =>
    appeler<ClientReconnu[]>(`/api/clients/reconnaissance${parametres({ q })}`, { signal }),
  affairesDuClient: (id: string, signal?: AbortSignal) =>
    appeler<AffaireReconnue[]>(`/api/clients/${id}/affaires`, { signal }),
  clients: (filtres: FiltresListe = {}, signal?: AbortSignal) =>
    appeler<Page<Client>>(`/api/clients${parametres(filtres)}`, { signal }),
  client: (id: string, signal?: AbortSignal) =>
    appeler<FicheClient>(`/api/clients/${id}`, { signal }),
  corrigerClient: (id: string, correction: Record<string, unknown>) =>
    appeler<Client>(`/api/clients/${id}`, { methode: "PATCH", corps: correction }),

  // --- LOT 2 -----------------------------------------------------------------
  fournisseurs: (filtres: FiltresListe & { statut?: string } = {}, signal?: AbortSignal) =>
    appeler<Page<Fournisseur>>(`/api/fournisseurs${parametres(filtres)}`, { signal }),
  /**
   * Une adresse et non un appel : le navigateur télécharge le fichier lui-même,
   * avec le cookie de session, comme il ouvre un document.
   */
  adresseExportFournisseurs: (filtres: { q?: string; statut?: string } = {}) =>
    `/api/fournisseurs/export.csv${parametres(filtres)}`,
  fournisseur: (id: string, signal?: AbortSignal) =>
    appeler<FicheFournisseur>(`/api/fournisseurs/${id}`, { signal }),
  annuaireEntreprises: (q: string) =>
    appeler<{ propositions: PropositionAnnuaire[] }>(`/api/fournisseurs/annuaire${parametres({ q })}`),
  ressemblancesFournisseur: (saisie: { nom?: string; email?: string }) =>
    appeler<{ ressemblances: RessemblanceFournisseur[] }>(
      `/api/fournisseurs/ressemblances${parametres(saisie)}`,
    ),
  creerFournisseur: (saisie: Record<string, unknown>) =>
    appeler<FicheFournisseur>("/api/fournisseurs", { methode: "POST", corps: saisie }),
  corrigerFournisseur: (id: string, correction: Record<string, unknown>) =>
    appeler<FicheFournisseur>(`/api/fournisseurs/${id}`, { methode: "PATCH", corps: correction }),
  changerStatutFournisseur: (id: string, statut: StatutFournisseur) =>
    appeler<FicheFournisseur>(`/api/fournisseurs/${id}/statut`, {
      methode: "POST",
      corps: { statut },
    }),
  ajouterContactFournisseur: (id: string, contact: Record<string, unknown>) =>
    appeler<FicheFournisseur>(`/api/fournisseurs/${id}/contacts`, {
      methode: "POST",
      corps: contact,
    }),
  retirerContactFournisseur: (id: string, contactId: string) =>
    appeler<FicheFournisseur>(`/api/fournisseurs/${id}/contacts/${contactId}`, {
      methode: "DELETE",
    }),
  /** Ticket #76 : toutes les versions des conditions commerciales de la fiche. */
  conditionsCommerciales: (id: string, signal?: AbortSignal) =>
    appeler<ConditionsDeLaFiche>(`/api/fournisseurs/${id}/conditions-commerciales`, { signal }),
  ajouterConditionsCommerciales: (id: string, saisie: SaisieConditions) =>
    appeler<ConditionsCommerciales>(`/api/fournisseurs/${id}/conditions-commerciales`, {
      methode: "POST",
      corps: saisie,
    }),
  corrigerConditionsCommerciales: (
    id: string,
    conditionId: string,
    correction: Partial<SaisieConditions>,
  ) =>
    appeler<ConditionsCommerciales>(
      `/api/fournisseurs/${id}/conditions-commerciales/${conditionId}`,
      { methode: "PATCH", corps: correction },
    ),
  validerConditionsCommerciales: (id: string, conditionId: string) =>
    appeler<ConditionsCommerciales>(
      `/api/fournisseurs/${id}/conditions-commerciales/${conditionId}/validation`,
      { methode: "POST" },
    ),
  joindreJustificatifConditions: (id: string, conditionId: string, fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    return televerser<PieceConditions>(
      `/api/fournisseurs/${id}/conditions-commerciales/${conditionId}/pieces`,
      formulaire,
    );
  },
  historiqueConditionsCommerciales: (id: string, signal?: AbortSignal) =>
    appeler<{ evenements: EvenementConditions[] }>(
      `/api/fournisseurs/${id}/conditions-commerciales/historique`,
      { signal },
    ),

  commandesFournisseurs: (
    filtres: {
      commande_client_id?: string;
      lot_id?: string;
      statut?: string;
      /** Ticket #20 : celles qui attendent encore un AR. */
      attendent_un_ar?: boolean;
      limite?: number;
    } = {},
    signal?: AbortSignal,
  ) =>
    appeler<Page<CommandeFournisseur>>(
      `/api/commandes-fournisseurs${parametres({ ...filtres, attendent_un_ar: filtres.attendent_un_ar ? "true" : undefined })}`,
      { signal },
    ),
  lignesFournisseurDuLot: (lotId: string, signal?: AbortSignal) =>
    appeler<LigneFournisseurDuLot[]>(
      `/api/commandes-fournisseurs/lignes${parametres({ lot_id: lotId })}`,
      { signal },
    ),
  /** Depuis une commande client, ou pour compléter une commande fournisseur. */
  commandesProposees: (
    depuis: { commande_client_id: string } | { commande_fournisseur_id: string },
    signal?: AbortSignal,
  ) =>
    appeler<CommandeClientProposee[]>(
      `/api/commandes-fournisseurs/commandes-proposees${parametres(depuis)}`,
      { signal },
    ),
  commandeFournisseur: (id: string, signal?: AbortSignal) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}`, { signal }),
  /** Ticket #78 : le franco d'un brouillon ; exige de voir les montants. */
  controleFranco: (id: string, signal?: AbortSignal) =>
    appeler<ControleFranco>(`/api/commandes-fournisseurs/${id}/franco`, { signal }),
  /** Ticket #22 : les commandes parties qui attendent encore une réception. */
  commandesAReceptionner: (signal?: AbortSignal) =>
    appeler<CommandeAReceptionner[]>("/api/receptions-logistiques/a-recevoir", { signal }),
  /** Une réception par part ; plusieurs parts font un BL commun, dont le fichier suit. */
  constaterReception: (saisie: SaisieReception, bl: File | null) => {
    const formulaire = new FormData();
    formulaire.append("saisie", JSON.stringify(saisie));
    if (bl) formulaire.append("bl", bl);
    return televerser<ReceptionLogistique[]>("/api/receptions-logistiques", formulaire);
  },
  /** Ticket #24 : la logistique dépose un BL ; LABGEST le lit. */
  deposerBl: (fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    formulaire.append("type_attendu", "BL_FOURNISSEUR");
    return televerser<ImportBl>("/api/imports-ocr", formulaire);
  },
  /** La file des imports que l'appelant valide ; l'écran n'en garde que les BL. */
  importsAValider: (signal?: AbortSignal) =>
    appeler<ImportBl[]>("/api/imports-ocr", { signal }),
  propositionReception: (id: string, signal?: AbortSignal) =>
    appeler<PropositionReception>(`/api/imports-ocr/${id}/proposition-reception`, { signal }),
  /** Valider le BL constate sa réception (ADR-0038). */
  validerBl: (id: string, saisie: SaisieBl) =>
    appeler<ImportBl>(`/api/imports-ocr/${id}/validation`, {
      methode: "POST",
      corps: { donnees_validees: saisie },
    }),
  /** `lignes` : les lignes de la réception que la photo montre ; aucune, la réception entière. */
  joindrePhotoReception: (id: string, fichier: File, lignes: string[] = []) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    for (const ligne of lignes) formulaire.append("lignes", ligne);
    return televerser<ReceptionLogistique>(
      `/api/receptions-logistiques/${id}/photos`,
      formulaire,
    );
  },
  corrigerReception: (id: string, correction: CorrectionReception) =>
    appeler<ReceptionLogistique>(`/api/receptions-logistiques/${id}`, {
      methode: "PATCH",
      corps: correction,
    }),
  creerCommandeFournisseur: (saisie: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>("/api/commandes-fournisseurs", {
      methode: "POST",
      corps: saisie,
    }),
  corrigerCommandeFournisseur: (id: string, correction: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}`, {
      methode: "PATCH",
      corps: correction,
    }),
  ajouterLigneFournisseur: (id: string, ligne: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/lignes`, {
      methode: "POST",
      corps: ligne,
    }),
  corrigerLigneFournisseur: (id: string, ligneId: string, correction: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/lignes/${ligneId}`, {
      methode: "PATCH",
      corps: correction,
    }),
  retirerLigneFournisseur: (id: string, ligneId: string) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/lignes/${ligneId}`, {
      methode: "DELETE",
    }),
  envoyerCommandeFournisseur: (id: string) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/envoi`, {
      methode: "POST",
    }),
  envoyerCommandeFournisseurParCourriel: (id: string, contactId: string) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/envoi-courriel`, {
      methode: "POST",
      corps: { contact_id: contactId },
    }),
  annulerCommandeFournisseur: (id: string, motif?: string) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/annulation`, {
      methode: "POST",
      corps: motif ? { motif } : undefined,
    }),
  porterFrais: (
    id: string,
    saisie: { nature: NatureFrais; libelle: string; montant_ht: number; motif?: string },
  ) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/frais`, {
      methode: "POST",
      corps: saisie,
    }),
  retirerFrais: (id: string, fraisId: string) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/frais/${fraisId}`, {
      methode: "DELETE",
    }),
  annulerReliquat: (id: string, ligneId: string, saisie: { quantite: number; motif: string }) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/lignes/${ligneId}/annulations`,
      { methode: "POST", corps: saisie },
    ),
  commanderAilleurs: (id: string, ligneId: string, saisie: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/lignes/${ligneId}/commande-ailleurs`,
      { methode: "POST", corps: saisie },
    ),
  ouvrirProblemeFournisseur: (
    id: string,
    saisie: {
      type: TypeProblemeFournisseur;
      description: string;
      ligne_commande_fournisseur_id: string | null;
    },
  ) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/problemes`, {
      methode: "POST",
      corps: saisie,
    }),
  demanderAvoir: (id: string, saisie: SaisieDemandeAvoir) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/demandes-avoir`, {
      methode: "POST",
      corps: saisie,
    }),
  abandonnerAvoir: (id: string, demandeId: string, motif: string) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/demandes-avoir/${demandeId}/abandon`,
      { methode: "POST", corps: { motif } },
    ),
  /** Ticket #47 : en négociation, le montant se corrige ; #49 : attendue, avec un motif. */
  corrigerMontantAvoir: (id: string, demandeId: string, montant_ht: number, motif?: string) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/demandes-avoir/${demandeId}`,
      { methode: "PATCH", corps: motif === undefined ? { montant_ht } : { montant_ht, motif } },
    ),
  /** Ticket #47 : le fournisseur accepte ; la demande est attendue, son montant figé. */
  accepterAvoir: (id: string, demandeId: string) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/demandes-avoir/${demandeId}/acceptation`,
      { methode: "POST" },
    ),
  /** Tickets #44 et #47 : en négociation et attendues, dont l'appelant voit la commande client. */
  avoirsEnCours: (signal?: AbortSignal) =>
    appeler<DemandeAvoir[]>("/api/commandes-fournisseurs/avoirs-en-cours", { signal }),
  /** Tickets #39 et #46 : ceux que l'appelant peut décider, autoriser ou confirmer. */
  ecartsPrixAValider: (signal?: AbortSignal) =>
    appeler<EcartPrix[]>("/api/commandes-fournisseurs/ecarts-prix", { signal }),
  /** Ticket #51 : les hausses de frais acceptées depuis trente jours, sur ses commandes client. */
  haussesFrais: (signal?: AbortSignal) =>
    appeler<HausseFrais[]>("/api/commandes-fournisseurs/hausses-frais", { signal }),
  /**
   * Accepter, ou confirmer un écart autorisé : sous la permission et le plafond. Sur une
   * ligne, la nature du prix (ticket #72) ; des frais n'en portent pas.
   */
  deciderEcartPrix: (
    id: string,
    ecartId: string,
    saisie: { motif: string; nature_prix?: NaturePrix },
  ) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/ecarts-prix/${ecartId}/decision`,
      { methode: "POST", corps: saisie },
    ),
  /** Ticket #59 : contester, avec un motif ; ouvre un problème fournisseur. */
  contesterEcartPrix: (id: string, ecartId: string, saisie: { motif: string }) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/ecarts-prix/${ecartId}/contestation`,
      { methode: "POST", corps: saisie },
    ),
  autoriserEcartPrix: (id: string, ecartId: string) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/ecarts-prix/${ecartId}/autorisation`,
      { methode: "POST" },
    ),
  enregistrerArFournisseur: (id: string, saisie: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/ar`, {
      methode: "POST",
      corps: saisie,
    }),
  deposerArFournisseur: (id: string, fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    return televerser<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/ar/imports`,
      formulaire,
    );
  },
  /** Valider un import OCR produit son effet : pour un AR, l'AR enregistré (ADR-0038). */
  validerImportOcr: (id: string, donnees: Record<string, unknown>) =>
    appeler<unknown>(`/api/imports-ocr/${id}/validation`, {
      methode: "POST",
      corps: { donnees_validees: donnees },
    }),
  choisirCommandeImportOcr: (id: string, commandeFournisseurId: string) =>
    appeler<unknown>(`/api/imports-ocr/${id}/commande-fournisseur`, {
      methode: "POST",
      corps: { commande_fournisseur_id: commandeFournisseurId },
    }),
  /** Ticket #41 : les documents communs en partie rattachés. */
  documentsEnPartie: (signal?: AbortSignal) =>
    appeler<DocumentEnPartie[]>("/api/documents-entrants/en-partie", { signal }),
  documentsEntrants: (etat: EtatDocumentEntrant, signal?: AbortSignal) =>
    appeler<DocumentEntrant[]>(`/api/documents-entrants${parametres({ etat })}`, { signal }),
  rattacherDocumentEntrant: (id: string, commandeFournisseurId: string) =>
    appeler<DocumentEntrant>(`/api/documents-entrants/${id}/rattachement`, {
      methode: "POST",
      corps: { commande_fournisseur_id: commandeFournisseurId },
    }),
  ecarterDocumentEntrant: (id: string, motif: string) =>
    appeler<DocumentEntrant>(`/api/documents-entrants/${id}/ecart`, {
      methode: "POST",
      corps: { motif },
    }),
  /** Ticket #64 : l'AR rattaché à la mauvaise commande ; son fichier retourne en file. */
  corrigerRattachementImportOcr: (id: string, motif: string) =>
    appeler<{ document_entrant_id: string; commande_fournisseur_id: string }>(
      `/api/imports-ocr/${id}/correction-rattachement`,
      { methode: "POST", corps: { motif } },
    ),
  rejeterImportOcr: (id: string, motif: string) =>
    appeler<unknown>(`/api/imports-ocr/${id}/rejet`, { methode: "POST", corps: { motif } }),
  corrigerArFournisseur: (id: string, arId: string, correction: Record<string, unknown>) =>
    appeler<FicheCommandeFournisseur>(`/api/commandes-fournisseurs/${id}/ar/${arId}`, {
      methode: "PATCH",
      corps: correction,
    }),
  resoudreProblemeFournisseur: (
    id: string,
    problemeId: string,
    resolution: string,
    montantAvoirHt: number | null = null,
  ) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/problemes/${problemeId}/resolution`,
      { methode: "POST", corps: { resolution, montant_avoir_ht: montantAvoirHt } },
    ),
  /** Ticket #23 : trancher une non-conformité ; la remise dit son montant. */
  deciderNonConformite: (
    id: string,
    problemeId: string,
    saisie: { decision: DecisionNonConformite; motif: string; montant_remise_ht: number | null },
  ) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/problemes/${problemeId}/decision`,
      { methode: "POST", corps: saisie },
    ),

  /**
   * Ticket #65 (décision 60) : rouvrir un problème résolu, avec le motif de cette
   * réouverture. La base dit ce qui l'empêche : la demande d'avoir à abandonner
   * d'abord, le surplus à rouvrir d'abord, la permission d'une non-conformité.
   */
  rouvrirProbleme: (id: string, problemeId: string, motif: string) =>
    appeler<FicheCommandeFournisseur>(
      `/api/commandes-fournisseurs/${id}/problemes/${problemeId}/reouverture`,
      { methode: "POST", corps: { motif } },
    ),

  // Ceux à qui une commande se rattache ou se transfère : le serveur lit leur
  // famille (ADR-0035), l'écran ne nomme aucun profil.
  proprietaires: (signal?: AbortSignal) =>
    appeler<Collegue[]>("/api/utilisateurs?proprietaires=true", { signal }),
  // Ceux qui peuvent recevoir une habilitation : le serveur
  // lit leur famille, l'écran ne la recopie pas.
  admissibles: (signal?: AbortSignal) =>
    appeler<Collegue[]>("/api/utilisateurs?admissibles=true", { signal }),

  habilitations: (commande_client_id: string, signal?: AbortSignal) =>
    appeler<Habilitation[]>(`/api/habilitations${parametres({ commande_client_id })}`, {
      signal,
    }),
  habiliter: (habilitation: {
    beneficiaire_id: string;
    commande_client_id: string;
    niveau: NiveauAcces;
    du: string | null;
    au: string | null;
    motif: string | null;
  }) => appeler<Habilitation>("/api/habilitations", { methode: "POST", corps: habilitation }),
  modifierHabilitation: (id: string, modification: ModificationHabilitation) =>
    appeler<Habilitation>(`/api/habilitations/${id}`, { methode: "PATCH", corps: modification }),
  revoquerHabilitation: (id: string, motif: string | null) =>
    appeler<Habilitation>(`/api/habilitations/${id}/revocation`, {
      methode: "POST",
      corps: { motif },
    }),

  affaires: (filtres: FiltresListe & { client_id?: string } = {}, signal?: AbortSignal) =>
    appeler<Page<AffaireAbregee>>(`/api/affaires${parametres(filtres)}`, { signal }),
  affaire: (id: string, signal?: AbortSignal) =>
    appeler<FicheAffaire>(`/api/affaires/${id}`, { signal }),

  // --- LOT 1 ter : l'administration des profils (ADR-0035) ----------------------
  profilsAdministres: (signal?: AbortSignal) =>
    appeler<ProfilDetaille[]>("/api/administration/profils", { signal }),
  creerProfil: (code: string, reglage: ReglageProfil) =>
    appeler<ProfilDetaille>("/api/administration/profils", {
      methode: "POST",
      corps: { code, ...reglage },
    }),
  reglerProfil: (code: string, reglage: ReglageProfil) =>
    appeler<ProfilDetaille>(`/api/administration/profils/${code}`, {
      methode: "PUT",
      corps: reglage,
    }),
  catalogueDesPermissions: (signal?: AbortSignal) =>
    appeler<PermissionCatalogue[]>("/api/administration/permissions", { signal }),
  creerCleVisibilite: (libelle: string, ouverteAuxExternes: boolean) =>
    appeler<PermissionCatalogue>("/api/administration/cles-visibilite", {
      methode: "POST",
      corps: { libelle, ouverte_aux_externes: ouverteAuxExternes },
    }),
  typesOcr: (signal?: AbortSignal) =>
    appeler<string[]>("/api/administration/types-ocr", { signal }),
  typesValidables: (signal?: AbortSignal) =>
    appeler<TypeValidable[]>("/api/administration/types-validables", { signal }),
  comptes: (signal?: AbortSignal) => appeler<Compte[]>("/api/administration/comptes", { signal }),
  attribuerProfil: (compteId: string, code: string) =>
    appeler<Compte>(`/api/administration/comptes/${compteId}/profils`, {
      methode: "POST",
      corps: { code },
    }),
  retirerProfil: (compteId: string, code: string) =>
    appeler<Compte>(`/api/administration/comptes/${compteId}/profils/${code}`, {
      methode: "DELETE",
    }),
  changerNature: (compteId: string, nature: Nature) =>
    appeler<Compte>(`/api/administration/comptes/${compteId}/nature`, {
      methode: "PUT",
      corps: { nature },
    }),

  registreReferentiels: (signal?: AbortSignal) =>
    appeler<EntreeRegistre[]>("/api/referentiels", { signal }),
  referentiel: (nom: string, actives = false, signal?: AbortSignal) =>
    appeler<Referentiel>(`/api/referentiels/${nom}${actives ? "?actives=true" : ""}`, {
      signal,
    }),
  creerValeurReferentiel: (nom: string, code: string, libelle: string) =>
    appeler<Referentiel>(`/api/referentiels/${nom}`, {
      methode: "POST",
      corps: { code, libelle },
    }),
  corrigerValeurReferentiel: (nom: string, id: string, correction: Record<string, unknown>) =>
    appeler<Referentiel>(`/api/referentiels/${nom}/${id}`, {
      methode: "PATCH",
      corps: correction,
    }),
  ordonnerReferentiel: (nom: string, ids: string[]) =>
    appeler<Referentiel>(`/api/referentiels/${nom}/ordre`, { methode: "PUT", corps: { ids } }),
  reglerTypeDocument: (id: string, reglages: ReglagesTypeDocument) =>
    appeler<Referentiel>(`/api/referentiels/types-document/${id}/reglages`, {
      methode: "PUT",
      corps: reglages,
    }),

  modelesDocument: (signal?: AbortSignal) =>
    appeler<ModeleDocumentResume[]>("/api/modeles-document", { signal }),
  modeleDocument: (id: string, signal?: AbortSignal) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}`, { signal }),
  creerModeleDocument: (nom: string, type_document: string) =>
    appeler<ModeleDocument>("/api/modeles-document", {
      methode: "POST",
      corps: { nom, type_document },
    }),
  corrigerModeleDocument: (id: string, correction: { nom?: string; actif?: boolean }) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}`, { methode: "PATCH", corps: correction }),
  ouvrirVersionModele: (id: string) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}/versions`, { methode: "POST" }),
  enregistrerVersionModele: (id: string, versionId: string, definition: DefinitionModele) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}/versions/${versionId}/definition`, {
      methode: "PUT",
      corps: { definition },
    }),
  publierVersionModele: (id: string, versionId: string) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}/versions/${versionId}/publication`, {
      methode: "POST",
    }),
  retirerVersionModele: (id: string, versionId: string) =>
    appeler<ModeleDocument>(`/api/modeles-document/${id}/versions/${versionId}/retrait`, {
      methode: "POST",
    }),

  fichesDuLot: (lotId: string, signal?: AbortSignal) =>
    appeler<FicheResume[]>(`/api/lots/${lotId}/fiches`, { signal }),
  modelesDisponibles: (signal?: AbortSignal) =>
    appeler<ModeleDisponible[]>("/api/fiches/modeles", { signal }),
  creerFiche: (lotId: string, modeleId: string) =>
    appeler<Fiche>(`/api/lots/${lotId}/fiches`, {
      methode: "POST",
      corps: { modele_id: modeleId },
    }),
  fiche: (id: string, signal?: AbortSignal) => appeler<Fiche>(`/api/fiches/${id}`, { signal }),
  enregistrerFiche: (id: string, valeurs: Record<string, unknown>) =>
    appeler<Fiche>(`/api/fiches/${id}/valeurs`, { methode: "PUT", corps: { valeurs } }),
  validerFiche: (id: string) =>
    appeler<Fiche>(`/api/fiches/${id}/validation`, { methode: "POST" }),
  abandonnerFiche: (id: string) => appeler<Fiche>(`/api/fiches/${id}/abandon`, { methode: "POST" }),
  /** Le tracé part en PNG, encodé en base 64, sans l'en-tête `data:` du canevas. */
  signerFiche: (
    id: string,
    signature: { signataire_nom: string; signataire_type: QualiteSignataire; trace_png: string },
  ) => appeler<Fiche>(`/api/fiches/${id}/signatures`, { methode: "POST", corps: signature }),
  deposerMediaFiche: (id: string, zone: string, fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    formulaire.append("zone", zone);
    return televerser<MediaFiche>(`/api/fiches/${id}/medias`, formulaire);
  },
  retirerMediaFiche: (id: string, mediaId: string) =>
    appeler<void>(`/api/fiches/${id}/medias/${mediaId}`, { methode: "DELETE" }),

  seuilsAlerte: (signal?: AbortSignal) => appeler<SeuilAlerte[]>("/api/seuils-alerte", { signal }),
  reglerSeuil: (id: string, valeur: string, actif: boolean) =>
    appeler<SeuilAlerte>(`/api/seuils-alerte/${id}`, {
      methode: "PUT",
      corps: { valeur, actif },
    }),

  recherche: (q: string, signal?: AbortSignal) =>
    appeler<Resultats>(`/api/recherche${parametres({ q })}`, { signal }),

  documents: (cible: CibleDocument, signal?: AbortSignal) =>
    appeler<Document[]>(`/api/documents${parametres({ ...cible })}`, { signal }),
  deposerDocument: (cible: CibleDocument, categorie: string, fichier: File) => {
    const formulaire = new FormData();
    formulaire.append("fichier", fichier);
    formulaire.append("categorie", categorie);
    for (const [cle, valeur] of Object.entries(cible)) formulaire.append(cle, valeur);
    return televerser<Document>("/api/documents", formulaire);
  },
  publierDocument: (id: string, visible_client: boolean) =>
    appeler<Document>(`/api/documents/${id}/publication`, {
      methode: "POST",
      corps: { visible_client },
    }),
};

/**
 * L'adresse du contenu d'un document.
 *
 * Un LIEN et non un `fetch` : le cookie de session part avec, le navigateur
 * gère le téléchargement, et rien ne transite par la mémoire de l'application.
 * Le point d'API répond `Content-Disposition: attachment` — c'est lui qui
 * décide que le fichier se télécharge au lieu de s'ouvrir dans l'onglet d'un
 * poste partagé (ADR-0011).
 */
export function urlContenuDocument(id: string) {
  return `/api/documents/${id}/contenu`;
}

/**
 * Ticket #111 : le PDF d'une fiche, rendu pour son lecteur — scellé s'il voit toutes les
 * sections d'une fiche figée, filtré et non scellé sinon. Un lien, comme un document.
 */
export function urlPdfFiche(id: string) {
  return `/api/fiches/${id}/pdf`;
}

export type FiltresListe = { q?: string; limite?: number; decalage?: number };

/**
 * Une chaîne de requête qui omet ce qui est vide — pas de `?q=` inutile.
 *
 * `decalage: 0` est volontairement omis : c'est le défaut du serveur, et une
 * adresse qui le porte n'apprend rien à personne. Les nombres sont convertis
 * ici plutôt qu'à chaque appel.
 */
function parametres(valeurs: Record<string, string | number | undefined>) {
  const utiles = Object.entries(valeurs)
    .filter(([, valeur]) => valeur !== undefined && valeur !== "" && valeur !== 0)
    .map(([cle, valeur]) => [cle, String(valeur)] as [string, string]);
  if (utiles.length === 0) return "";
  return `?${new URLSearchParams(utiles).toString()}`;
}
